import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import videojs from 'video.js';
import type Player from 'video.js/dist/types/player';
import 'video.js/dist/video-js.css';
import '../../styles/video-player.css';

// Imperative handle the parent drives — read the current playhead (for "Mark decision
// point"), seek back to a marked time, or pause. Keeps Video.js internals fully behind this
// component: callers depend on this small surface, not the player API.
export interface VideoPlayerHandle {
  getCurrentTimeMs(): number;
  seekToMs(ms: number): void;
  pause(): void;
}

interface VideoPlayerProps {
  // Signed HLS master-playlist URL (CloudFront). Adaptive bitrate is negotiated by Video.js's
  // bundled http-streaming engine (VHS) — no separate hls.js needed.
  src: string;
  poster?: string;
  className?: string;
  // Fired ONCE per watch — on the first play of a loaded source, and again after the clip ends
  // and is replayed, but NOT on pause/resume (that would inflate). Used to log a view.
  onFirstPlay?: () => void;
  // Fired when the source fails to load with a NETWORK error — most commonly an expired CloudFront
  // signed URL (1h TTL) that the tab outlived. The parent should refetch the playback query to mint
  // a fresh URL and pass it back as a new `src`; this component swaps it in and playback resumes.
  // Fired at most once per distinct `src`, so a genuinely broken clip won't loop.
  onSourceExpired?: () => void;
}

// ─── CloudFront signed-URL propagation for HLS sub-requests ──────────────────────────
//
// HLS manifests reference rendition playlists + .ts segments by RELATIVE path, so VHS resolves
// them to absolute URLs WITHOUT the signed `?Policy&Signature&Key-Pair-Id` the master URL
// carried — CloudFront then 403s them as unsigned. CloudFront signed *URLs* (unlike signed
// *cookies*) don't propagate automatically. So we re-attach the clip's signed query to every
// matching sub-request via a single GLOBAL VHS request hook.
//
// Why global (not per-player): VHS fires `xhr-hooks-ready` DURING tech construction, before
// `player.tech_` is assigned, so `player.tech().vhs` is undefined at that moment. The global
// `videojs.Vhs.xhr.onRequest` hook sidesteps that timing entirely. Per-clip queries live in a
// prefix→query map each player maintains; the hook is registered once for the app.

const signedQueryByPrefix = new Map<string, string>();
let globalHookInstalled = false;

// Minimal shape of the global VHS request-hook API — Video.js's types don't expose `Vhs.xhr`.
interface VhsGlobal {
  xhr?: { onRequest?: (cb: (options: { uri?: string }) => unknown) => void };
}

function installGlobalSigningHook(): void {
  if (globalHookInstalled) return;
  const vhs = (videojs as unknown as { Vhs?: VhsGlobal }).Vhs;
  if (!vhs?.xhr?.onRequest) return;
  globalHookInstalled = true;
  vhs.xhr.onRequest((options) => {
    const uri = options.uri;
    // Skip the master URL (already signed) and anything not under a known clip prefix.
    if (typeof uri === 'string' && !uri.includes('Signature=')) {
      for (const [prefix, query] of signedQueryByPrefix) {
        if (uri.startsWith(prefix)) {
          options.uri = uri + (uri.includes('?') ? '&' : '?') + query;
          break;
        }
      }
    }
    // callAllRequestHooks assigns the return value — must hand options back, modified or not.
    return options;
  });
}

// Split a signed master URL into its clip prefix (origin + `<uuid>/`) and query string, so the
// hook can match sub-requests under the same prefix and re-attach the signature.
function parseSigned(src: string): { prefix: string; query: string } | null {
  const q = src.indexOf('?');
  if (q < 0) return null;
  try {
    const u = new URL(src);
    const prefixPath = u.pathname.slice(0, u.pathname.lastIndexOf('/') + 1);
    return { prefix: `${u.origin}${prefixPath}`, query: src.slice(q + 1) };
  } catch {
    return null;
  }
}

// Reusable Video.js player. The ONLY module that imports video.js — the rest of the app sees
// just this component + VideoPlayerHandle (per the §4 "nothing duplicates a player lib" rule).
// The admin clip editor uses the handle to mark the decision point off the live playhead; the
// referee player (M6/M9) will reuse this and add the pause-at-decision-point behaviour.
export const VideoPlayer = forwardRef<VideoPlayerHandle, VideoPlayerProps>(function VideoPlayer(
  { src, poster, className = '', onFirstPlay, onSourceExpired },
  ref,
) {
  // Keep the latest callbacks in refs so the (once-registered) handlers always call the current
  // one without re-binding. firedRef gates onFirstPlay to one call per watch.
  const onFirstPlayRef = useRef(onFirstPlay);
  onFirstPlayRef.current = onFirstPlay;
  const onSourceExpiredRef = useRef(onSourceExpired);
  onSourceExpiredRef.current = onSourceExpired;
  const firedRef = useRef(false);
  // The src the error handler last requested recovery for — gates onSourceExpired to one fire per
  // distinct src, so a truly unplayable clip refetches once and then surfaces the error instead of
  // looping. currentSrcRef feeds the once-registered handler the live src without a stale closure.
  const currentSrcRef = useRef(src);
  const recoveredSrcRef = useRef<string | null>(null);
  // containerRef is a plain div React owns; the <video-js> node is created imperatively and
  // appended inside it. This is the video.js-recommended React pattern: React must NOT own the
  // media element, or its reconciliation fights Video.js's DOM mutations on dispose.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<Player | null>(null);
  // The clip prefix this player registered in the global map — tracked so a src swap / unmount
  // removes exactly its own entry.
  const prefixRef = useRef<string | null>(null);

  // Register this clip's signed query in the global map, replacing any prefix this player set
  // before (on a src swap).
  function registerSigned(nextSrc: string) {
    const parsed = parseSigned(nextSrc);
    if (prefixRef.current && prefixRef.current !== parsed?.prefix) {
      signedQueryByPrefix.delete(prefixRef.current);
    }
    if (parsed) {
      signedQueryByPrefix.set(parsed.prefix, parsed.query);
      prefixRef.current = parsed.prefix;
    }
  }

  // Initialise the player exactly once. The guard also absorbs React 18 StrictMode's
  // double-invoke of effects in dev (which would otherwise build two players).
  useEffect(() => {
    if (playerRef.current || !containerRef.current) return;

    // Install the signing hook BEFORE any source loads (the source is set by the effect below,
    // which runs after this one).
    installGlobalSigningHook();

    const videoEl = document.createElement('video-js');
    videoEl.classList.add('vjs-big-play-centered');
    containerRef.current.appendChild(videoEl);

    playerRef.current = videojs(videoEl, {
      controls: true,
      preload: 'auto',
      fluid: true, // responsive 16:9 box that fills the editor column
      playsinline: true,
    });

    // View logging: fire onFirstPlay once per watch. `play` also fires on resume-after-pause, so
    // firedRef guards to one call; `ended` re-arms it so a replay counts as a new watch. A source
    // swap re-arms too (in the source effect below).
    playerRef.current.on('play', () => {
      if (!firedRef.current) {
        firedRef.current = true;
        onFirstPlayRef.current?.();
      }
    });
    playerRef.current.on('ended', () => {
      firedRef.current = false;
    });

    // Recovery: a NETWORK error (MediaError code 2) on load is almost always an expired signed URL
    // (VHS 403s on the manifest, excludes the last playlist, then surfaces code 2). Ask the parent
    // for a fresh URL. Gate on currentSrcRef so we fire at most once per src — a new src re-arms it
    // (it won't match recoveredSrcRef), a genuinely dead clip stops after one attempt.
    playerRef.current.on('error', () => {
      const err = playerRef.current?.error();
      if (err?.code === 2 && recoveredSrcRef.current !== currentSrcRef.current) {
        recoveredSrcRef.current = currentSrcRef.current;
        onSourceExpiredRef.current?.();
      }
    });

    return () => {
      // Drop this player's prefix from the shared map, then tear down Video.js. Null the ref so
      // a StrictMode remount re-initialises cleanly instead of touching a disposed player.
      if (prefixRef.current) {
        signedQueryByPrefix.delete(prefixRef.current);
        prefixRef.current = null;
      }
      playerRef.current?.dispose();
      playerRef.current = null;
    };
    // Init-once: source/poster changes are handled by the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set/swap the source (also runs on first mount). Register the signed query FIRST so the
  // global hook can sign sub-requests the moment VHS starts fetching.
  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    // Track the live src for the once-registered error handler's recovery gate.
    currentSrcRef.current = src;
    registerSigned(src);
    // A new clip is a new watch — re-arm the view fire for the next play.
    firedRef.current = false;
    player.src({ src, type: 'application/x-mpegURL' });
    if (poster !== undefined) player.poster(poster);
  }, [src, poster]);

  useImperativeHandle(
    ref,
    () => ({
      // currentTime() returns seconds (or undefined before metadata loads) → whole ms.
      getCurrentTimeMs: () => Math.round((playerRef.current?.currentTime() ?? 0) * 1000),
      seekToMs: (ms) => {
        playerRef.current?.currentTime(ms / 1000);
      },
      pause: () => {
        playerRef.current?.pause();
      },
    }),
    [],
  );

  // data-vjs-player marks the container so Video.js doesn't wrap it a second time.
  return <div ref={containerRef} className={className} data-vjs-player />;
});
